import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { CompactTile, Messages, PromptConfirmation } from 'ui';
import { TileProps } from 'ui/tile/tile';
import { CasePermissions, ChangeConnectionSequence, ConstraintResponseType } from 'app/types-shared';
import { MdOutlineArrowDownward, MdOutlineArrowUpward } from 'react-icons/md';
import { shouldNavigateToModal } from 'app/components/helpers';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from 'app/area/shared/navigation';
import styles from './connect-case-selector.scss';

export interface ConnectedCasesTileTranslations {
  textMoveUp: string;
  textMoveDown: string;
  textOpenBowtie: string;
  textOpenCase: string;
  textPromptTitle: string;
  textPromptDescription: string;
  textPromptContinue: string;
  textPromptCancel: string;
}

export enum ResolveContextMode {
  Preview = 0,
  Edit = 1
}

export enum SequenceType {
  Up = 'up',
  Down = 'down'
}

export interface ConnectedCasesTileProps {
  urlOpenNew?: boolean;
}

export interface ConnectedCasesTileProps extends TileProps, ConnectedCasesTileTranslations {
  enabledConnectionOperations: { moveUp: boolean; moveDown: boolean };
  caseId: number;
  entityId: number;
  connectionTypeId: number;
  connectedDrafts?: any[];
  permissions: CasePermissions;
  changeConnectionSequence: ChangeConnectionSequence;
  readOnly?: boolean;
  resolveContext: {
    mode: ResolveContextMode;
  };
  moveConnectionUp: (steps: number, caseId?: number, allowWarnings?: boolean) => void;
  moveConnectionDown: (steps: number, caseId?: number, allowWarnings?: boolean) => void;
}

export const ConnectedCasesTile: React.FC<ConnectedCasesTileProps> = (props) => {

  const [warningsChangeSequence, setWarningsChangeSequence] = React.useState(undefined);

  const isPreview = props.permissions?.read && props.resolveContext?.mode === ResolveContextMode.Preview;

  const fetching = props.connectionTypeId === props.changeConnectionSequence?.caseId
    && props.changeConnectionSequence?.fetching;

  const disabledOverlay = !!props.changeConnectionSequence?.fetching;

   // Get current path and location from router
  const location = useLocation();
  const currentPath = location.pathname;
  const { query } = useQueryParams();

  const moveConnectionUp = () => {
    setWarningsChangeSequence(SequenceType.Up);
    isPreview
      ? (!props.changeConnectionSequence?.fetching && props.moveConnectionUp(1, props.caseId))
      : props.moveConnectionUp(1);
  };

  const moveConnectionDown = () => {
    setWarningsChangeSequence(SequenceType.Down);
    isPreview
      ? (!props.changeConnectionSequence?.fetching && props.moveConnectionDown(1, props.caseId))
      : props.moveConnectionDown(1);
  };

  const openBowtie = () => {
    const { primaryKey } = props;
    navigate.toBowtie(primaryKey.value, true);
  };

  const openCase = () => {
    const { primaryKey, urlOpenNew, params } = props;

    if (urlOpenNew) {
      navigate.toCaseView(primaryKey.value, false, true);
    } else {
      if (shouldNavigateToModal(!!params?.favouriteId, !!params?.dashboardType, !!params?.bowtieCaseId)) {
        navigate.toCaseViewModal(primaryKey.value, query, currentPath);
      }
      else {
        navigate.toCaseView(primaryKey.value);
      }
    }
  };

  const onContinueWarningChangeSequence = async () => {
    if (warningsChangeSequence === SequenceType.Up) {
      setWarningsChangeSequence(undefined);
      props.moveConnectionUp(1, props.caseId, true);
    }
    if (warningsChangeSequence === SequenceType.Down) {
      setWarningsChangeSequence(undefined);
      props.moveConnectionDown(1, props.caseId, true);
    }
  };

  const mappedWarningsChangeSequence = () =>
    props.changeConnectionSequence?.responseWarning?.map((warning, index: number) => {
      const message: Messages = { key: 0, label: '', message: '' };
      message.key = index;
      message.label = warning.domainText;
      message.message = warning.responseText.replace(warning.domainText + ': ', '');
      return message;
    });

  const { textMoveUp, textMoveDown, enabledConnectionOperations, enabledChildEntityOperations,
    textOpenBowtie, textOpenCase, textPromptTitle, textPromptDescription, textPromptContinue,
    textPromptCancel, permissions, ...restProps } = props;

  const displayChangeSequence = !!props.permissions?.edit && !!props.params?.id && !props.readOnly;

  const extendedContextMenuOperations = [];
  extendedContextMenuOperations.push({ text: textOpenCase, callback: openCase, icon: undefined });
  permissions && permissions.bowtie &&
    extendedContextMenuOperations.push({ text: textOpenBowtie, callback: openBowtie, icon: undefined });
  displayChangeSequence && enabledConnectionOperations?.moveUp &&
    extendedContextMenuOperations.push({ type: 'changeSequence', text: textMoveUp, 
      callback: moveConnectionUp, icon: <MdOutlineArrowUpward /> });
  displayChangeSequence && enabledConnectionOperations?.moveDown &&
    extendedContextMenuOperations.push({ type: 'changeSequence', text: textMoveDown, 
      callback: moveConnectionDown, icon: <MdOutlineArrowDownward /> });

  const hasWarning = props.changeConnectionSequence?.responseWarning
    ?.some(warning => warning.responseType === ConstraintResponseType.Warning);

  return <>
    <CompactTile
      {...restProps}
      className={styles.connectedCaseTile}
      extendedContextMenuOperations={extendedContextMenuOperations}
      enabledChildEntityOperations={{ ...enabledChildEntityOperations, ...{ add: false } }}
      fetching={fetching}
      disabledOverlay={disabledOverlay} />

    {!!warningsChangeSequence && hasWarning
      && <PromptConfirmation
        title={textPromptTitle}
        description={textPromptDescription}
        confirmMessages={mappedWarningsChangeSequence()}
        confirmationStyle={'warning'}
        continueText={textPromptContinue}
        onContinue={onContinueWarningChangeSequence}
        closeOnDone={false}
        onCancel={() => setWarningsChangeSequence(false)}
        cancelText={textPromptCancel} />}
  </>;
};
